/*eslint max-lines-per-function: ["error", 270]*/
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import isEqual from 'lodash/isEqual';

import { useNavigation } from '../../../common/navigation';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  GetInvoicePaymentDto,
  GetInvoicePaymentLinkDtoTypeEnum,
  GetReceiptDto,
  SaveLinkItemDto,
  SaveLinkItemDtoTypeEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import momencik from '../../../common/momencik';
import mathUtils from '../../../utils/mathUtils';
import numberFormatter from '../../../common/numberFormatter';
import { InvoicePaymentEditorTabs } from '../../containers/InvoicePaymentEditorView/InvoicePaymentEditorView';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';

import { LinksTable } from './LinksTable';
import { WithdrawalModal } from './WithdrawalModal';
import {
  PendingLink,
  sortBySystemNumber,
  toPending,
  toPendingFromInvoice,
  toPendingFromLink,
} from './InvoicePaymentLinksTypes';
import { AddLinkModal } from './AddLinkModal';

interface InvoicePaymentLinksProps {
  invoicePaymentId?: string;
  invoicePayment?: GetInvoicePaymentDto;
  paymentAmount?: string | null;
  onRemainingChange?: (remaining: number) => void;
  setNotSavedTab?: (tab: InvoicePaymentEditorTabs | undefined) => void;
}

const normalizeLinks = (links: PendingLink[]) =>
  links.map(l => ({
    ...l,
    amount: mathUtils.toFixed(l.amount || '0', 2),
    comment: (l.comment || '').trim(),
  }));

export const InvoicePaymentLinks: React.FC<
  InvoicePaymentLinksProps
> = props => {
  const [pendingLinks, setPendingLinks] = useState<PendingLink[]>([]);
  const originalLinks = useRef<PendingLink[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [receipt, setReceipt] = useState<GetReceiptDto | undefined>(undefined);
  const isLinkedToReceipt = !!receipt;
  const spd = useSpdCore();
  const nav = useNavigation();
  const { onRemainingChange, paymentAmount, setNotSavedTab } = props;

  useEffect(() => {
    setNotSavedTab?.(
      !isEqual(
        normalizeLinks(pendingLinks),
        normalizeLinks(originalLinks.current),
      )
        ? InvoicePaymentEditorTabs.LINKS
        : undefined,
    );
  }, [pendingLinks, setNotSavedTab]);

  useEffect(() => {
    if (!onRemainingChange) {
      return;
    }
    const visible = pendingLinks.filter(l => !l.toDelete);
    const totalLinked = mathUtils.add(...visible.map(l => l.amount || '0'));
    const payment = mathUtils.parse(paymentAmount ?? '0');
    onRemainingChange(mathUtils.subtract(payment, totalLinked));
  }, [pendingLinks, paymentAmount, onRemainingChange]);

  useEffect(() => {
    if (!props.invoicePaymentId) {
      return;
    }
    void spd.invoicePayments
      .getLinksByPaymentId(props.invoicePaymentId)
      .then(loaded => {
        const links = sortBySystemNumber(
          loaded.filter(l => l.active).map(toPending),
        );
        originalLinks.current = links;
        setPendingLinks(links);
      })
      .catch(console.error);
  }, [props.invoicePaymentId, spd.invoicePayments]);

  useEffect(() => {
    const receiptId = pendingLinks.find(l => l.receiptId)?.receiptId;
    if (!receiptId) {
      setReceipt(undefined);
      return;
    }
    void spd.receipts.getById(receiptId).then(setReceipt).catch(console.error);
  }, [pendingLinks, spd.receipts]);

  const handleAddFromModal = async (invoiceIds: string[]) => {
    const added: PendingLink[] = [];
    for (const id of invoiceIds) {
      if (pendingLinks.some(l => l.salesInvoiceId === id && !l.toDelete)) {
        continue;
      }
      const links = await spd.invoicePayments.getLinksByInvoiceId(id);
      const active = links.filter(l => l.active);
      if (active.length > 0) {
        added.push(toPendingFromLink(active[0], id));
      } else {
        const inv = await spd.salesInvoices.getById(id);
        added.push(toPendingFromInvoice(inv));
      }
    }
    setPendingLinks(prev => sortBySystemNumber([...prev, ...added]));
    setShowModal(false);
  };

  const handleWithdraw = (amount: string, comment?: string) => {
    const withdrawal: PendingLink = {
      key: `withdrawal-${Date.now()}`,
      type: GetInvoicePaymentLinkDtoTypeEnum.Withdrawal,
      salesInvoiceId: '',
      amount,
      comment: comment ?? null,
      originalComment: null,
      originalAmount: '',
      unlinkedAmount: '',
      toDelete: false,
    };
    setPendingLinks(prev => [...prev, withdrawal]);
    setShowWithdrawalModal(false);
  };

  const handleSave = async () => {
    if (!props.invoicePaymentId) {
      return;
    }
    setValidationErrors([]);
    setSaving(true);
    try {
      const links = pendingLinks.map(l => ({
        existingId: l.existingId ?? null,
        type: l.type as unknown as SaveLinkItemDtoTypeEnum,
        salesInvoiceId: l.salesInvoiceId || null,
        amount: l.amount,
        originalAmount: l.originalAmount,
        comment: l.comment ?? null,
        originalComment: l.originalComment ?? null,
        toDelete: l.toDelete,
      })) as SaveLinkItemDto[];
      const reloaded = await spd.invoicePayments.saveLinks(
        props.invoicePaymentId,
        { links },
      );
      const saved = sortBySystemNumber(
        reloaded.filter(l => l.active).map(toPending),
      );
      originalLinks.current = saved;
      setPendingLinks(saved);
    } catch (response) {
      setValidationErrors(await responseErrors(response as ResponseError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {validationErrors.length > 0 && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setValidationErrors([])}
        >
          <ul className="mb-0">
            {validationErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}
      {receipt && (
        <Alert variant="info" className="mb-3">
          <strong>
            Paragon nr {receipt.systemNumber} do badania nr:{' '}
            {receipt.exam?.number}
          </strong>
          {receipt.receiptDate && (
            <span className="ms-2 text-muted">
              — {momencik(receipt.receiptDate)}
            </span>
          )}
          <span className="ms-2">
            — kwota: {numberFormatter(receipt.amountGross, true) ?? '—'}
          </span>
        </Alert>
      )}
      {pendingLinks.length === 0 ? (
        <p className="text-muted">Brak powiązanych faktur.</p>
      ) : (
        <LinksTable
          links={pendingLinks}
          paymentAmount={props.paymentAmount}
          readOnly={isLinkedToReceipt}
          onAmountChange={(key, value) => {
            setPendingLinks(prev =>
              prev.map(l => (l.key === key ? { ...l, amount: value } : l)),
            );
          }}
          onCopyUnlinked={key => {
            setPendingLinks(prev =>
              prev.map(l => {
                if (l.key !== key) {
                  return l;
                }
                return {
                  ...l,
                  amount: mathUtils.toFixed(
                    mathUtils.add(
                      l.originalAmount || '0',
                      l.unlinkedAmount || '0',
                    ),
                    2,
                  ),
                };
              }),
            );
          }}
          onDelete={key => {
            setPendingLinks(prev =>
              prev.map(l =>
                l.key === key ? { ...l, toDelete: !l.toDelete } : l,
              ),
            );
          }}
          onNavigate={id => nav.salesInvoiceEditor(id, true)}
        />
      )}
      <div className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => setShowModal(true)}
            disabled={!props.invoicePaymentId || isLinkedToReceipt}
          >
            Dodaj powiązanie
          </Button>
          <Button
            variant="outline-warning"
            onClick={() => setShowWithdrawalModal(true)}
            disabled={!props.invoicePaymentId || isLinkedToReceipt}
          >
            Wyprowadź poza system
          </Button>
        </div>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !props.invoicePaymentId || isLinkedToReceipt}
        >
          {saving ? 'Zapisuję...' : 'Zapisz'}
        </Button>
      </div>

      {props.invoicePaymentId && (
        <AddLinkModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onConfirm={ids => void handleAddFromModal(ids)}
          paymentId={props.invoicePaymentId}
          paymentSender={props.invoicePayment?.sender}
          paymentTitle={props.invoicePayment?.title}
        />
      )}
      <WithdrawalModal
        show={showWithdrawalModal}
        onHide={() => setShowWithdrawalModal(false)}
        onConfirm={handleWithdraw}
      />
    </>
  );
};
